import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { ArticleSchema } from '@/components/feature/SeoSchema';

export default function BasetaoBuyingGuide() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen">
      <Header />
      <ArticleSchema slug="basetao-buying-guide" />
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-900 transition-colors whitespace-nowrap">Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Complete Buying Guide</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase text-primary-700 bg-primary-100 rounded-full">Buying Guide</span>
              <span className="text-xs text-foreground-500">Updated August 2026</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Basetao Buying Guide 2026 — How to Shop Smart Step by Step
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Whether you are a first-time user or an experienced international shopper, having a clear <strong>Basetao buying guide</strong> ensures you avoid common pitfalls and get the best possible results from every purchase. This step-by-step walkthrough covers the entire process from product discovery to receiving your package.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Step-by-Step Basetao Shopping Process</h2>
              <div className="space-y-5">
                {[
                  { step: '1', title: 'Browse and Discover', desc: 'Use the Basetao Spreadsheet categories or the main Basetao platform to find products that interest you. Pay attention to trending items, user reviews, and price comparisons across similar products.' },
                  { step: '2', title: 'Place Your Order', desc: 'Once you have identified products you want, place your order through the Basetao platform. You will need to fund your account or pay for the items at this stage.' },
                  { step: '3', title: 'Warehouse Arrival and QC', desc: 'Your items are shipped to the Basetao warehouse. Staff photograph each item for quality control. Review these photos carefully before approving international shipment.' },
                  { step: '4', title: 'Consolidate and Ship', desc: 'If you have multiple items, consolidate them into one package. Choose your preferred shipping method based on cost, speed, and reliability. Pay the international shipping fee.' },
                  { step: '5', title: 'Track and Receive', desc: 'Monitor your package using the provided tracking number. Delivery times vary by shipping method and destination country.' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold flex-shrink-0">{item.step}</span>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">FAQ</h2>
              <div className="space-y-5">
                {[
                  { q: 'Do I need a Basetao account to shop?', a: 'Yes, you need to create a free Basetao account to place orders and access warehouse services. Registration is quick and straightforward.' },
                  { q: 'What payment methods does Basetao accept?', a: 'Basetao accepts various payment methods including credit cards, PayPal, and other international payment options. Check the platform for the most current list.' },
                ].map((faq, idx) => (
                  <div key={idx}><h3 className="text-base font-semibold text-foreground-900 mb-1.5">{faq.q}</h3><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-300 whitespace-nowrap"><i className="ri-home-4-line"></i>Back to Homepage</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-100 hover:bg-background-200 border border-background-300/60 rounded-full transition-colors duration-300 whitespace-nowrap">Visit Main Website<i className="ri-external-link-line"></i></a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}